import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { aiMatchApi } from '@/api/ai';
import {
  aiRefusal,
  ErrorCodes,
  isAiWalletRefusal,
  isApiError,
  type AiRefusal,
  type AiWalletReason,
} from '@/api/errors';
import type { AutomationGateState } from '@/features/automation/useAutomationGate';
import { useMe } from '@/session/meHooks';

type AutomationGate = AutomationGateState;

const UNAVAILABLE_CODES = [
  'AI_DISABLED',
  'AI_BUDGET_EXCEEDED',
  'AI_UNAVAILABLE',
  'AI_TIMEOUT',
  'AI_CAP_EXCEEDED',
  'UPSTREAM_UNAVAILABLE',
];

/**
 * "Suggest matches" for one account (MM-D-45): the account page's kebab item. The result is a set of
 * proposals a human confirms in the Review card; `onDone` reloads it (TMMAPP-0105 §C 4).
 */
export function useSuggestMatches({
  accountId,
  gate,
  onNotify,
  onDone,
}: {
  accountId: number;
  gate: AutomationGate;
  onNotify: (message: string) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const inFlight = useRef(false);
  const [suggesting, setSuggesting] = useState(false);
  // A wallet refusal (TMMAPI-0151) is the in-place prompt the page shows, not a toast.
  const [walletRefusal, setWalletRefusal] = useState<
    (AiRefusal & { reason: AiWalletReason }) | null
  >(null);
  const off =
    !me.user.aiEnabled ||
    gate.catalog?.groups.some((group) =>
      group.items.some((item) => item.key === 'AI_MATCH_SUGGESTION' && !item.enabled),
    ) === true;
  const catalogMissing = !gate.loading && gate.catalog === null;
  const reason = off
    ? t('review.suggestOff')
    : gate.loading
      ? t('common.loading')
      : catalogMissing
        ? t('review.suggestSettingsUnavailable')
        : null;

  async function suggest() {
    // A failed settings load is retried from the same item.
    if (catalogMissing) {
      void gate.reload();
      return;
    }
    if (off || gate.loading || gate.catalog === null || inFlight.current) return;
    if (gate.needsConsent) {
      void navigate('/me/automation-help');
      return;
    }
    inFlight.current = true;
    setSuggesting(true);
    setWalletRefusal(null);
    try {
      const proposals = await aiMatchApi.suggestMatches(accountId);
      onDone();
      onNotify(
        proposals.length > 0
          ? t('review.suggestedCount', { count: proposals.length })
          : t('review.noNewMatches'),
      );
    } catch (error) {
      const refusal = aiRefusal(error);
      if (isAiWalletRefusal(refusal)) {
        setWalletRefusal(refusal);
        return;
      }
      const code = isApiError(error) ? error.code : '';
      onNotify(
        code === ErrorCodes.RATE_LIMITED
          ? t('review.suggestRateLimited')
          : UNAVAILABLE_CODES.includes(code)
            ? t('review.suggestUnavailable')
            : t('planned.errorGeneric'),
      );
    } finally {
      inFlight.current = false;
      setSuggesting(false);
    }
  }

  return {
    suggest,
    suggesting,
    walletRefusal,
    reason,
    /** Never enabled while a request runs, settings load, or AI is off; a missing catalog retries. */
    disabled: suggesting || gate.loading || off,
  };
}
